// Pannello di configurazione integrazione XAtlas — Controllo accessi
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Input } from '../../../components/ui/Input'
import { Button } from '../../../components/ui/Button'
import { CredentialsNote } from './CredentialsNote'
import api from '../../../lib/api'

const schema = z.object({
  syncIntervalMinutes: z.coerce.number().int().min(5).max(1440).default(60),
  alertSeverityFilter: z.enum(['all', 'medium', 'high', 'critical']).default('high'),
  enableBadgeSync: z.boolean().default(true),
})

type FormValues = z.infer<typeof schema>

interface XAtlasConfigProps {
  currentMetadata?: Record<string, unknown>
  onSaved?: () => void
}

export function XAtlasConfig({ currentMetadata, onSaved }: XAtlasConfigProps) {
  const qc = useQueryClient()

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      syncIntervalMinutes: (currentMetadata?.['syncIntervalMinutes'] as number) ?? 60,
      alertSeverityFilter: (currentMetadata?.['alertSeverityFilter'] as FormValues['alertSeverityFilter']) ?? 'high',
      enableBadgeSync: (currentMetadata?.['enableBadgeSync'] as boolean) ?? true,
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      await api.put('/integrations/xatlas/config', { metadata: data })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations'] })
      onSaved?.()
    },
  })

  return (
    <div className="space-y-5">
      <CredentialsNote envVars={['XATLAS_API_URL', 'XATLAS_API_KEY']} />

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Intervallo sincronizzazione presenze (minuti)
          </label>
          <Input
            type="number"
            min={5}
            max={1440}
            {...register('syncIntervalMinutes')}
            error={errors.syncIntervalMinutes?.message}
          />
          <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
            Ogni quanti minuti sincronizzare le presenze verso XAtlas. Min: 5, Max: 1440 (24h).
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Filtro severity alert accessi
          </label>
          <select
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
            {...register('alertSeverityFilter')}
          >
            <option value="all">Tutti gli alert</option>
            <option value="medium">Medium e superiori</option>
            <option value="high">High e superiori</option>
            <option value="critical">Solo critici</option>
          </select>
          <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
            Solo gli alert con questa severity o superiore vengono mostrati nel dashboard.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="enableBadgeSync"
            className="h-4 w-4 rounded border-[var(--fc-border)]"
            {...register('enableBadgeSync')}
          />
          <label htmlFor="enableBadgeSync" className="text-sm text-[var(--fc-foreground)]">
            Abilita sincronizzazione badge digitali bidirezionale
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onSaved}>
            Annulla
          </Button>
          <Button type="submit" isLoading={saveMutation.isPending}>
            Salva configurazione
          </Button>
        </div>
      </form>
    </div>
  )
}
